import axios from 'axios';
import type { dynamicObj } from 'src/pages/assets';
import { ApiClient } from 'src/core/ApiClient';
import { objLen } from 'src/utils/general';
import { ApiConfiguration, RequestConfig } from 'src/types';
import {
  getOrgId,
  addOrgIdQueryString,
  addOrgIdToUrl,
} from 'src/utils/organization/org_id_helper';
import { logger } from 'src/utils/logger';

export default class AbstractApiService extends ApiClient {
  protected token = '';
  public constructor(apiConfiguration: ApiConfiguration) {
    super(apiConfiguration);
  }

  protected encodeQueryData(data: any) {
    const ret = [];
    for (const d in data) {
      ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
    }

    return ret.join('&');
  }

  protected async getAuthTokenStore() {
    const { useAuthTokenStore } = await import('src/store/main');
    return useAuthTokenStore();
  }

  protected async getUserStore() {
    const { useUserStore } = await import('src/store/main');
    return useUserStore();
  }

  protected async getDocumentStore() {
    const { useDocumentStore } = await import('src/store/main');
    return useDocumentStore();
  }

  async postWithCancel<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    if (payload instanceof FormData) {
      payload.append('org_id', this.getOrgId());
    } else {
      payload.org_id = this.getOrgId();
    }

    return super.postWithCancel(path, payload, options);
  }

  async post<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    if (payload instanceof FormData) {
      payload.append('org_id', this.getOrgId());
    } else {
      payload.org_id = this.getOrgId();
    }

    return super.post(path, payload, options);
  }

  async put<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    if (payload instanceof FormData) {
      payload.append('org_id', this.getOrgId());
    } else {
      payload.org_id = this.getOrgId();
    }

    return super.put(path, payload, options);
  }

  async patch<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    if (payload instanceof FormData) {
      payload.append('org_id', this.getOrgId());
    } else {
      payload.org_id = this.getOrgId();
    }

    return super.patch(path, payload, options);
  }

  async get<TRequest, TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    const optionsDefault = {} as dynamicObj;
    if (!config) {
      config = {} as RequestConfig;
    }
    await this.addTokenToOptions(optionsDefault, config);
    const options = { ...optionsDefault, ...config };

    path = this.addOrgIdQueryString(path);

    return super.get(path, options);
  }

  async getWithOrgIDInUrl<TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    const optionsDefault = {} as dynamicObj;
    if (!config) {
      config = {} as RequestConfig;
    }
    await this.addTokenToOptions(optionsDefault, config);

    const options = { ...optionsDefault, ...config };

    path = this.addOrgIdToUrl(path);
    return super.get(path, options);
  }

  async getWithoutOrgId<TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    // Direct API call without automatic org_id addition
    // Used for Loki API calls where org_id is included in LogQL query
    const optionsDefault = {} as dynamicObj;
    if (!config) {
      config = {} as RequestConfig;
    }
    await this.addTokenToOptions(optionsDefault, config);
    const options = { ...optionsDefault, ...config };

    // Skip org_id addition - call super.get directly
    return super.get(path, options);
  }

  async postWithoutOrgId<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    // Direct API POST call without automatic org_id addition
    // Used for endpoints where org_id is extracted from JWT token
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    // Skip org_id addition - call super.post directly
    return super.post(path, payload, options);
  }

  async deleteWithoutOrgId<TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    // Direct API DELETE call without automatic org_id addition
    // Used for endpoints where org_id is extracted from JWT token
    const optionsDefault = {} as dynamicObj;
    if (!config) {
      config = {} as RequestConfig;
    }
    await this.addTokenToOptions(optionsDefault, config);
    const options = { ...optionsDefault, ...config };

    // Skip org_id addition - call super.delete directly
    return super.delete(path, options);
  }

  // getDefaultOptions () {
  //   return {
  //     timeout: 30000,
  //     timeoutErrorMessage: 'Request timed out',
  //   }
  // }

  async delete<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    config?: RequestConfig,
  ): Promise<TResponse> {
    if (!config) {
      config = {} as RequestConfig;
    }

    const options = this.getDefaultOptions(config, payload);
    await this.addTokenToOptions(options, {});

    // Check if payload has actual content (not just empty object)
    // For DELETE requests with empty payloads, we don't add org_id to body
    // The org_id is already in the query string via addOrgIdQueryString()
    const hasPayloadContent =
      payload instanceof FormData
        ? payload.keys().next().value !== undefined
        : payload && Object.keys(payload).length > 0;

    if (hasPayloadContent) {
      // Only add org_id to payload if it has other content
      if (payload instanceof FormData) {
        payload.append('org_id', this.getOrgId());
      } else {
        payload.org_id = this.getOrgId();
      }
      this.setContentTypeIfFormData(payload, config);
      options.data = payload;
    }
    // If payload is empty {}, do NOT send it as body for DELETE
    // DELETE requests should have no body per HTTP standards

    path = this.addOrgIdQueryString(path);
    return super.delete(path, options);
  }

  private attachOrgId(payload: any): void {
    if (payload instanceof FormData) {
      payload.append('org_id', this.getOrgId());
    } else {
      payload.org_id = this.getOrgId();
    }
  }

  private setContentTypeIfFormData(payload: any, config: RequestConfig): void {
    if (payload instanceof FormData) {
      config.headers = config.headers ?? {};
      config.headers['Content-Type'] = 'multipart/form-data';
    }
  }

  async addTokenToOptions(optionsDefault: dynamicObj, config: dynamicObj) {
    optionsDefault.headers = optionsDefault.headers || {};

    const orgId = this.getOrgId();

    if (config && config.headers && config.headers.Authorization) {
      return;
    }

    if (optionsDefault.headers && optionsDefault.headers.Authorization) {
      return;
    }

    const token = await this.getToken();
    if (objLen(config) !== 0) {
      if (!config.hasOwnProperty('headers')) {
        config.headers = {};
      }

      if (!config.headers['X-ORG-ID'] && orgId) {
        config.headers['X-ORG-ID'] = orgId;
      }

      if (
        !config.headers.hasOwnProperty('Authorization') ||
        config.headers.Authorization === ''
      ) {
        config.headers.Authorization = token;
      }
      return;
    }

    if (
      !optionsDefault.headers.hasOwnProperty('Authorization') ||
      optionsDefault.headers.Authorization === ''
    ) {
      optionsDefault.headers.Authorization = token;
    }

    if (!optionsDefault.headers['X-ORG-ID'] && orgId) {
      optionsDefault.headers['X-ORG-ID'] = orgId;
    }
  }

  getDefaultOptions(config: dynamicObj, payload: FormData | dynamicObj) {
    let optionsDefault = {} as dynamicObj;
    if (objLen(config) !== 0) {
      optionsDefault = config;
    }

    if (!optionsDefault.hasOwnProperty('headers')) {
      optionsDefault.headers = {};
    }

    if (payload instanceof FormData) {
      optionsDefault.headers['Content-Type'] = 'multipart/form-data';
    } else {
      optionsDefault.headers['Content-Type'] = 'application/json';
    }

    return optionsDefault;
  }

  async getToken() {
    const { useAuthTokenStore } = await import('src/store/main');
    const auth_token_store = useAuthTokenStore();
    let { token } = auth_token_store;
    if (!token) {
      // Try to refresh token if available, but don't fail if method doesn't exist (for tests)
      if (typeof auth_token_store.setAuthToken === 'function') {
        try {
          await auth_token_store.setAuthToken();
        } catch (error: any) {
          logger.warn(
            '[AUTH] Token refresh failed in getToken:',
            error.message,
          );
          // Continue with empty token - API will handle 401 error
        }
      }
    }
    // CRITICAL FIX: Get current_token and add "Bearer " prefix
    // This ensures the Authorization header is properly formatted
    // We use current_token directly without calling getAuthorizationToken() to avoid
    // token refresh/validation which could invalidate the token during API calls
    token = auth_token_store.current_token;
    if (token && !token.startsWith('Bearer ')) {
      token = 'Bearer ' + token;
    }
    return token;
  }

  async getSubOrgId() {
    const { useUserStore, useDocumentStore } = await import('src/store/main');
    const user_store = useUserStore();
    const document_store = useDocumentStore();
    let sub_org_id = await user_store.getSubOrgId();
    if (document_store.record.sub_org_id) {
      sub_org_id = document_store.record.sub_org_id;
    }
    return sub_org_id;
  }

  async getParentOrgId() {
    const { useUserStore } = await import('src/store/main');
    const user_store = useUserStore();
    const parent_ord_id = user_store.getParentOrgId();
    return parent_ord_id;
  }

  getOrgId() {
    return getOrgId();
  }

  addOrgIdToUrl(url: string) {
    return addOrgIdToUrl(url);
  }

  addOrgIdQueryString(url: string) {
    return addOrgIdQueryString(url);
  }

  public async executeMultipleGetRequests(
    urlObj: { url: string; queryString: string; name: string }[],
  ): Promise<any[]> {
    const token = await this.getToken();

    const requestOptions = {
      headers: {
        Authorization: token,
      },
    };

    /// Creating an array of Axios GET requests
    const requests = urlObj.map((item) =>
      axios
        .get(`${item.url}${item.queryString || ''}`, requestOptions)
        .then((response) => ({
          url: item.url,
          name: item.name,
          data: response.data,
        }))
        .catch((error) => ({ url: item.url, error: error.message })),
    );

    return await Promise.all(requests);
  }

  public async executeMultiplePostRequests(
    urlObj: { url: string; name: string; data?: any }[],
  ): Promise<any[]> {
    const token = await this.getToken();

    const requestOptions = {
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    };

    // Creating an array of Axios POST requests
    const requests = urlObj.map((item) =>
      axios
        .post(item.url, item.data || {}, requestOptions)
        .then((response) => ({
          url: item.url,
          name: item.name,
          data: response.data.data,
          pages: response.data.pages,
          total_records: response.data.total_records,
        }))
        .catch((error) => ({
          url: item.url,
          name: item.name,
          error: error.message,
        })),
    );

    return await Promise.all(requests);
  }
}
